import React, { useState } from "react";
import "./SystemPreferences.css";

export default function SystemPreferences() {
  const [preferences, setPreferences] = useState({
    timezone: "(UTC +05:30) Asia/Kolkata",
    dateFormat: "June 19, 2026 (MMMM DD, YYYY)",
    timeFormat: "10:45 AM (12-hour)",
    language: "English (US)",
    pageSize: "A4",
    signatureType: "Quantum Trust Stamp + Signature",
    itemsPerPage: "10",

    darkMode: false,

    strongPassword: true,
    autoLogout: "30 minutes",
    reAuth: true,
    ipRestriction: false,
    sessionTimeout: "8 hours",
    twoFA: true,

    signaturePosition: "Bottom Right",
    timestampEnabled: true,
    timestampType: "I-PQSign Quantum Trust Stamp",
    hashAlgorithm: "SHA-256",
  });

  const handleChange = (e) => {
    setPreferences({
      ...preferences,
      [e.target.name]: e.target.value,
    });
  };

  const handleToggle = (field) => {
    setPreferences({
      ...preferences,
      [field]: !preferences[field],
    });
  };

  return (
    <div className="system-page">

      <div className="preferences-grid">

        {/* GENERAL PREFERENCES */}
        <div className="pref-card">
          <h3>General Preferences</h3>

          <PreferenceRow
            label="Default Time Zone"
            control={
              <select
                name="timezone"
                value={preferences.timezone}
                onChange={handleChange}
              >
                <option>(UTC +05:30) Asia/Kolkata</option>
                <option>(UTC +00:00) London</option>
                <option>(UTC -05:00) New York</option>
              </select>
            }
          />

          <PreferenceRow
            label="Date Format"
            control={
              <select
                name="dateFormat"
                value={preferences.dateFormat}
                onChange={handleChange}
              >
                <option>June 19, 2026 (MMMM DD, YYYY)</option>
                <option>19/06/2026</option>
                <option>06/19/2026</option>
              </select>
            }
          />

          <PreferenceRow
            label="Time Format"
            control={
              <select
                name="timeFormat"
                value={preferences.timeFormat}
                onChange={handleChange}
              >
                <option>10:45 AM (12-hour)</option>
                <option>22:45 (24-hour)</option>
              </select>
            }
          />

          <PreferenceRow
            label="Language"
            control={
              <select
                name="language"
                value={preferences.language}
                onChange={handleChange}
              >
                <option>English (US)</option>
                <option>English (UK)</option>
                <option>Hindi</option>
              </select>
            }
          />

          <PreferenceRow
            label="Default Page Size for PDFs"
            control={
              <select
                name="pageSize"
                value={preferences.pageSize}
                onChange={handleChange}
              >
                <option>A4</option>
                <option>A3</option>
                <option>Letter</option>
              </select>
            }
          />

          <PreferenceRow
            label="Default Signature Type"
            control={
              <select
                name="signatureType"
                value={preferences.signatureType}
                onChange={handleChange}
              >
                <option>Quantum Trust Stamp + Signature</option>
                <option>Digital Signature</option>
              </select>
            }
          />

          <PreferenceRow
            label="Enable Dark Mode"
            description="Enable dark mode for the platform."
            control={
              <ToggleSwitch
                checked={preferences.darkMode}
                onChange={() => handleToggle("darkMode")}
              />
            }
          />

          <PreferenceRow
            label="Items Per Page"
            control={
              <select
                name="itemsPerPage"
                value={preferences.itemsPerPage}
                onChange={handleChange}
              >
                <option>10</option>
                <option>25</option>
                <option>50</option>
              </select>
            }
          />
        </div>

        {/* SECURITY */}
        <div className="pref-card">
          <h3>Security Preferences</h3>

          <PreferenceRow
            label="Enforce Strong Password Policy"
            description="Require strong passwords for all users."
            control={
              <ToggleSwitch
                checked={preferences.strongPassword}
                onChange={() => handleToggle("strongPassword")}
              />
            }
          />

          <PreferenceRow
            label="Auto Logout"
            description="Automatically logout inactive users."
            control={
              <select
                name="autoLogout"
                value={preferences.autoLogout}
                onChange={handleChange}
              >
                <option>15 minutes</option>
                <option>30 minutes</option>
                <option>1 hour</option>
              </select>
            }
          />

          <PreferenceRow
            label="Require Re-authentication"
            description="Require re-authentication for sensitive actions."
            control={
              <ToggleSwitch
                checked={preferences.reAuth}
                onChange={() => handleToggle("reAuth")}
              />
            }
          />

          <PreferenceRow
            label="IP Restriction"
            description="Restrict access based on IP addresses."
            control={
              <ToggleSwitch
                checked={preferences.ipRestriction}
                onChange={() => handleToggle("ipRestriction")}
              />
            }
          />

          <PreferenceRow
            label="Session Timeout"
            description="Automatically end user sessions."
            control={
              <select
                name="sessionTimeout"
                value={preferences.sessionTimeout}
                onChange={handleChange}
              >
                <option>4 hours</option>
                <option>8 hours</option>
                <option>12 hours</option>
              </select>
            }
          />

          <PreferenceRow
            label="Two-Factor Authentication"
            description="Require 2FA for all users."
            control={
              <ToggleSwitch
                checked={preferences.twoFA}
                onChange={() => handleToggle("twoFA")}
              />
            }
          />
        </div>
      </div>

      {/* DOCUMENT PREFERENCES */}
      <div className="pref-card full-width">
        <h3>Document Preferences</h3>

        <PreferenceRow
          label="Default Signature Position"
          control={
            <select
              name="signaturePosition"
              value={preferences.signaturePosition}
              onChange={handleChange}
            >
              <option>Bottom Right</option>
              <option>Bottom Left</option>
              <option>Top Right</option>
              <option>Top Left</option>
            </select>
          }
        />

        <PreferenceRow
          label="Add Timestamp to Documents"
          description="Automatically add timestamp to signed documents."
          control={
            <div className="timestamp-row">
              <ToggleSwitch
                checked={preferences.timestampEnabled}
                onChange={() => handleToggle("timestampEnabled")}
              />

              <select
                name="timestampType"
                value={preferences.timestampType}
                onChange={handleChange}
              >
                <option>I-PQSign Quantum Trust Stamp</option>
                <option>Standard Timestamp</option>
              </select>
            </div>
          }
        />

        <PreferenceRow
          label="Default Hash Algorithm (Classical)"
          control={
            <select
              name="hashAlgorithm"
              value={preferences.hashAlgorithm}
              onChange={handleChange}
            >
              <option>SHA-256</option>
              <option>SHA-384</option>
              <option>SHA-512</option>
            </select>
          }
        />
      </div>
    </div>
  );
}

function PreferenceRow({ label, description, control }) {
  return (
    <div className="pref-row">
      <div className="pref-label">
        <div>{label}</div>
        {description && (
          <small>{description}</small>
        )}
      </div>
      <div>{control}</div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
      />
      <span className="slider"></span>
    </label>
  );
}